<template>
  <div class="calc">
    <!-- Header -->
    <header class="top reveal" style="--d: 0ms">
      <div class="brand">
        <img
          src="@/assets/n-zero-logo.png"
          alt="Net Zero Fuels"
          class="brand__logo"
        />
        <div class="brand__txt">
          <span class="brand__eyebrow">Net Zero Fuels</span>
          <h1 class="brand__title">Carbon Savings Calculator</h1>
        </div>
      </div>
      <span class="badge">
        <svg viewBox="0 0 24 24" class="badge__tick" aria-hidden="true">
          <path
            d="M5 12.5l4.5 4.5L19 7"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        ISCC&nbsp;verified
      </span>
    </header>

    <!-- Controls -->
    <section class="panel reveal" style="--d: 90ms">
      <div class="field">
        <label class="field__label">Biodiesel blend</label>
        <div class="segs" role="group" aria-label="Select biodiesel blend">
          <button
            v-for="b in blends"
            :key="b.value"
            type="button"
            class="seg"
            :class="{ 'seg--on': selectedBlend === b.value }"
            :aria-pressed="selectedBlend === b.value"
            @click="selectedBlend = b.value"
          >
            <span class="seg__code">{{ b.code }}</span>
            <span class="seg__sub">{{ b.sub }}</span>
          </button>
        </div>
      </div>

      <div class="field">
        <label class="field__label" for="litres">Monthly consumption</label>
        <div class="amount">
          <input
            id="litres"
            class="amount__input"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            v-model="formattedLitres"
            @input="updateLitres"
          />
          <span class="amount__unit">litres&nbsp;/&nbsp;month</span>
        </div>
      </div>
    </section>

    <!-- Hero result -->
    <section class="hero reveal" style="--d: 180ms">
      <span class="hero__eyebrow">Annual CO₂ avoided</span>
      <div class="hero__figure">
        <CountUp class="hero__num" :value="emissionSavings" :duration="1100" />
        <span class="hero__unit">kg CO₂</span>
      </div>

      <div class="meter">
        <div class="meter__track">
          <div class="meter__fill" :style="{ width: meterWidth }">
            <span class="meter__spark"></span>
          </div>
        </div>
        <p class="meter__caption">
          <strong><CountUp :value="percentSavings" :decimals="1" />%</strong>
          lower lifecycle emissions than fossil diesel
          <span class="meter__basis">volumetric basis · ISCC-verified</span>
        </p>
      </div>
    </section>

    <!-- Equivalencies -->
    <section class="equiv">
      <article class="eq eq--trees reveal" style="--d: 270ms">
        <div class="eq__icon" aria-hidden="true">
          <svg viewBox="0 0 64 64">
            <defs>
              <linearGradient id="leaf" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#e3ff9c" />
                <stop offset="1" stop-color="#86d23e" />
              </linearGradient>
            </defs>
            <path
              d="M32 60 C32 48 32 42 32 33"
              fill="none"
              stroke="#a8e85c"
              stroke-width="3.4"
              stroke-linecap="round"
            />
            <path
              d="M31 41 C21 41 14.5 34 13.5 22.5 C25.5 22.5 32.5 30.5 31 41 Z"
              fill="url(#leaf)"
            />
            <path
              d="M33 35 C43 35 51 27 52 15.5 C40 15.5 31.5 24 33 35 Z"
              fill="url(#leaf)"
            />
          </svg>
        </div>
        <div class="eq__body">
          <CountUp class="eq__num" :value="treesPlanted" />
          <p class="eq__label">tree seedlings grown for 10 years</p>
        </div>
      </article>

      <article class="eq eq--cars reveal" style="--d: 350ms">
        <div class="eq__icon" aria-hidden="true">
          <svg viewBox="0 0 64 64">
            <defs>
              <linearGradient id="car" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#9be0fa" />
                <stop offset="1" stop-color="#3aa6da" />
              </linearGradient>
            </defs>
            <path
              d="M19 33 L23 23 C24 20.6 25.7 19.5 28.4 19.5 H37.6 C40.3 19.5 42 20.6 43 23 L47 33 Z"
              fill="url(#car)"
              opacity="0.6"
            />
            <rect x="9" y="32" width="46" height="13" rx="6" fill="url(#car)" />
            <circle cx="21" cy="46" r="5.6" fill="#11271f" />
            <circle cx="43" cy="46" r="5.6" fill="#11271f" />
            <circle cx="21" cy="46" r="2.2" fill="#d6f3ff" />
            <circle cx="43" cy="46" r="2.2" fill="#d6f3ff" />
          </svg>
        </div>
        <div class="eq__body">
          <CountUp class="eq__num" :value="carsRemoved" />
          <p class="eq__label">fossil-fuel cars off the road for a year</p>
        </div>
      </article>
    </section>

    <!-- Fine print -->
    <p class="fineprint reveal" style="--d: 440ms">
      Figures are approximate and may vary slightly with the emirate of
      refining. Calculations use Neutral Fuels' ISCC-certified biodiesel, which
      is independently GHG-verified each year. Equivalencies follow the US&nbsp;EPA
      Greenhouse Gas Equivalencies methodology.
    </p>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { emissionData, CO2PerTreeSeedling10yr } from "../data/emissionData";
import CountUp from "./CountUp.vue";

const blends = [
  { value: "B100 (UCOME/FAME)", code: "B100", sub: "UCOME · FAME" },
  { value: "B20", code: "B20", sub: "20% blend" },
  { value: "B7", code: "B7", sub: "7% blend" },
  { value: "B5", code: "B5", sub: "5% blend" },
];
const selectedBlend = ref("B100 (UCOME/FAME)");

const litres = ref(100000);
const formattedLitres = ref("100,000");

watch(litres, (newVal) => {
  formattedLitres.value = newVal.toLocaleString();
});

function updateLitres(event) {
  const value = event.target.value.replace(/,/g, "");
  litres.value = isNaN(Number(value)) ? 0 : Number(value);
}

const emissionSavings = computed(() => {
  const data = emissionData[selectedBlend.value];
  return litres.value * data.savingsEmissionFactor * 12;
});

const dieselEmission = computed(
  () => litres.value * emissionData["B0"].emissionFactor
);

const percentSavings = computed(() =>
  dieselEmission.value
    ? (emissionSavings.value / 12 / dieselEmission.value) * 100
    : 0
);

const treesPlanted = computed(
  () => emissionSavings.value / CO2PerTreeSeedling10yr
);

const avgCarCO2 = 4600; // EPA: average passenger vehicle, kg CO₂ per year
const carsRemoved = computed(() => emissionSavings.value / avgCarCO2);

const meterWidth = computed(
  () => `${Math.max(0, Math.min(100, percentSavings.value))}%`
);
</script>

<style scoped>
.calc {
  display: flex;
  flex-direction: column;
  gap: clamp(16px, 2.4vw, 22px);
  min-width: 0;
}

/* ---------- Header ---------- */
.top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: clamp(2px, 1vw, 8px);
}

.brand {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.brand__logo {
  height: 54px;
  width: auto;
  filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.35));
}

.brand__txt {
  display: flex;
  flex-direction: column;
  line-height: 1;
}

.brand__eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--volt);
}

.brand__title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: clamp(1.15rem, 3.4vw, 1.6rem);
  letter-spacing: -0.015em;
  color: var(--text);
  margin: 6px 0 0;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: var(--volt-soft);
  background: rgba(200, 255, 74, 0.12);
  border: 1px solid rgba(200, 255, 74, 0.32);
  box-shadow: 0 10px 24px -18px rgba(200, 255, 74, 0.6);
}

.badge__tick {
  width: 15px;
  height: 15px;
  color: var(--volt);
}

/* ---------- Panel (controls) ---------- */
.panel {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: clamp(20px, 3vw, 30px);
  box-shadow: var(--shadow-soft);
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: clamp(20px, 2.6vw, 26px);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.field {
  min-width: 0;
}

.field__label {
  display: block;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 12px;
}

.segs {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.seg {
  appearance: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  padding: 14px 14px 13px;
  border-radius: var(--radius-sm);
  border: 1.5px solid var(--line);
  background: rgba(255, 255, 255, 0.04);
  color: var(--text);
  min-width: 0;
  overflow: hidden;
  transition: transform 0.25s cubic-bezier(0.2, 0.7, 0.2, 1),
    box-shadow 0.25s ease, background 0.25s ease, border-color 0.25s ease,
    color 0.25s ease;
}

.seg:hover {
  border-color: rgba(200, 255, 74, 0.5);
  transform: translateY(-2px);
}

.seg__code {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.05rem;
  letter-spacing: -0.01em;
}

.seg__sub {
  font-size: 0.68rem;
  font-weight: 600;
  color: var(--muted);
  letter-spacing: 0.01em;
}

.seg--on {
  background: linear-gradient(135deg, #d6ff7a, #c8ff4a);
  border-color: transparent;
  color: #1a2616;
  transform: translateY(-2px);
  box-shadow: 0 16px 32px -14px rgba(200, 255, 74, 0.55);
}

.seg--on .seg__sub {
  color: rgba(26, 38, 22, 0.72);
}

/* ---------- Amount input ---------- */
.amount {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 4px 2px 12px;
  border-bottom: 2.5px solid var(--line);
  transition: border-color 0.25s ease;
}

.amount:focus-within {
  border-color: var(--volt);
}

.amount__input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(1.8rem, 6vw, 2.6rem);
  letter-spacing: -0.02em;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.amount__input::placeholder {
  color: var(--muted-2);
}

.amount__unit {
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--muted);
  white-space: nowrap;
}

/* ---------- Hero ---------- */
.hero {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius);
  padding: clamp(24px, 3.6vw, 38px) clamp(22px, 3.4vw, 38px);
  background:
    radial-gradient(120% 130% at 100% 0%, rgba(116, 208, 234, 0.12) 0%, transparent 46%),
    radial-gradient(120% 130% at 0% 100%, rgba(200, 255, 74, 0.1) 0%, transparent 48%),
    var(--card-2);
  border: 1px solid var(--line);
  box-shadow: var(--shadow-soft);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.hero__eyebrow {
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--sky);
}

.hero__figure {
  display: flex;
  align-items: baseline;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 8px;
  min-width: 0;
}

.hero__num {
  font-family: var(--font-display);
  font-weight: 900;
  font-size: clamp(1.95rem, 8.5vw, 5.4rem);
  line-height: 0.96;
  letter-spacing: -0.04em;
  background: linear-gradient(102deg, #c8ff4a 0%, #a6f08a 42%, #74d0ea 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: #c8ff4a;
}

.hero__unit {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: clamp(1rem, 3.2vw, 1.35rem);
  color: var(--text);
}

/* ---------- Savings meter ---------- */
.meter {
  margin-top: clamp(20px, 2.6vw, 26px);
}

.meter__track {
  height: 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.32);
  overflow: hidden;
}

.meter__fill {
  position: relative;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #c8ff4a 0%, #79d9b0 55%, #74d0ea 100%);
  box-shadow: 0 0 18px -2px rgba(200, 255, 74, 0.5);
  transition: width 1s cubic-bezier(0.22, 0.7, 0.2, 1);
}

.meter__spark {
  position: absolute;
  right: 3px;
  top: 50%;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fff;
  transform: translateY(-50%);
  box-shadow: 0 0 10px 2px rgba(255, 255, 255, 0.9);
}

.meter__caption {
  margin: 14px 0 0;
  font-size: 0.95rem;
  color: var(--muted);
  line-height: 1.5;
}

.meter__caption strong {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.15rem;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.meter__basis {
  display: block;
  margin-top: 3px;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--volt);
}

/* ---------- Equivalency cards ---------- */
.equiv {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(14px, 2.2vw, 20px);
}

.eq {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: clamp(20px, 2.8vw, 28px);
  border-radius: var(--radius);
  border: 1px solid var(--line);
  box-shadow: var(--shadow-soft);
  transition: transform 0.32s cubic-bezier(0.2, 0.7, 0.2, 1),
    box-shadow 0.32s ease;
  min-width: 0;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.eq__body {
  min-width: 0;
}

.eq:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lift);
}

.eq--trees {
  background: linear-gradient(150deg, rgba(200, 255, 74, 0.14) 0%, rgba(255, 255, 255, 0.04) 72%);
}

.eq--cars {
  background: linear-gradient(150deg, rgba(116, 208, 234, 0.16) 0%, rgba(255, 255, 255, 0.04) 72%);
}

.eq__icon {
  flex: none;
  width: 64px;
  height: 64px;
  display: grid;
  place-items: center;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.06);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
}

.eq__icon svg {
  width: 42px;
  height: 42px;
  transition: transform 0.4s cubic-bezier(0.2, 0.7, 0.2, 1);
  transform-origin: bottom center;
}

.eq--trees:hover .eq__icon svg {
  transform: rotate(-5deg);
}

.eq--cars:hover .eq__icon svg {
  transform: translateX(3px);
}

.eq__num {
  display: block;
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(1.7rem, 5.6vw, 2.5rem);
  line-height: 1;
  letter-spacing: -0.03em;
  color: var(--text);
}

.eq__label {
  margin: 7px 0 0;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--muted);
  line-height: 1.35;
}

/* ---------- Fine print ---------- */
.fineprint {
  margin: 6px clamp(4px, 1.5vw, 14px) 0;
  font-size: 0.76rem;
  line-height: 1.6;
  color: var(--muted-2);
}

/* ---------- Reveal animation ---------- */
.reveal {
  opacity: 0;
  transform: translateY(16px);
  animation: rise 0.75s cubic-bezier(0.2, 0.7, 0.2, 1) forwards;
  animation-delay: var(--d, 0ms);
}

@keyframes rise {
  to {
    opacity: 1;
    transform: none;
  }
}

/* ---------- Responsive ---------- */
@media (max-width: 560px) {
  .segs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .equiv {
    grid-template-columns: minmax(0, 1fr);
  }
  .top {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }
  .brand__logo {
    height: 44px;
  }
  .badge {
    padding: 7px 11px;
    font-size: 0.72rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .reveal {
    animation: none;
    opacity: 1;
    transform: none;
  }
  .seg,
  .eq,
  .eq__icon svg,
  .meter__fill {
    transition: none;
  }
}
</style>
